/**
 * Column-mapping persistence + "do we even need the map step" logic for the
 * migration wizard (wmi_plan §8). The map step only appears when an uploaded
 * sheet's headers don't already match the WagePro field names; the chosen
 * mapping is remembered per entity so a repeat import skips it.
 *
 * ONLY UI preferences live here — a header-name map. NEVER a token or any
 * credential (per docs/security-rules.md: nothing auth-shaped in
 * localStorage). A header map is a non-secret draft/preference blob.
 *
 * Pure except for the explicit `Storage` argument, so it is unit-testable with
 * an in-memory store and never reaches for `window` implicitly.
 */
import {
  MIGRATION_SHEETS,
  columnsForJurisdiction,
  type MigrationJurisdiction,
} from '@jumpstone/core';
import { normalizeHeaderKey } from '@jumpstone/spreadsheet';
import type { MigrationSheetKind } from './migration_upload_logic.js';

/** A per-sheet file-header → target-field map, keyed by source sheet name. */
export type SheetMappings = Record<string, Record<string, string>>;

const STORAGE_PREFIX = 'wp.migration.mapping:';

function storageKey(entityId: string): string {
  return `${STORAGE_PREFIX}${entityId}`;
}

/**
 * The target field names valid for a sheet kind + jurisdiction, plus the
 * empty-string "ignore" sentinel the mapping UI offers.
 */
export function targetFieldsFor(
  kind: MigrationSheetKind,
  jurisdiction: MigrationJurisdiction,
): string[] {
  return columnsForJurisdiction(MIGRATION_SHEETS[kind], jurisdiction).map(
    (c) => c.name,
  );
}

/**
 * Whether every header in `headers` already matches a target field for the
 * sheet (case/space/underscore-insensitively). When true for every classified
 * sheet, the wizard skips the map step entirely.
 */
export function headersAlreadyMatch(
  headers: string[],
  kind: MigrationSheetKind,
  jurisdiction: MigrationJurisdiction,
): boolean {
  const targets = new Set(
    targetFieldsFor(kind, jurisdiction).map((t) => normalizeHeaderKey(t)),
  );
  return headers.every((h) => targets.has(normalizeHeaderKey(h)));
}

/**
 * Build the default mapping for a sheet: each file header mapped to the target
 * field whose normalized name matches, or `''` (ignore) when nothing matches.
 * This seeds the map UI so the common case is pre-filled.
 */
export function defaultMapping(
  headers: string[],
  kind: MigrationSheetKind,
  jurisdiction: MigrationJurisdiction,
): Record<string, string> {
  const targetsByNormalized = new Map<string, string>(
    targetFieldsFor(kind, jurisdiction).map((t) => [normalizeHeaderKey(t), t]),
  );
  const mapping: Record<string, string> = {};
  for (const header of headers) {
    mapping[header] = targetsByNormalized.get(normalizeHeaderKey(header)) ?? '';
  }
  return mapping;
}

/** Load saved mappings for an entity. Never throws; returns {} on any error. */
export function loadSavedMappings(
  storage: Storage,
  entityId: string,
): SheetMappings {
  try {
    const raw = storage.getItem(storageKey(entityId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return {};
    return parsed as SheetMappings;
  } catch (err) {
    console.warn('[wagepro-web] migration: could not read saved mapping:', err);
    return {};
  }
}

/** Persist mappings for an entity. Best-effort; a storage error is swallowed. */
export function saveMappings(
  storage: Storage,
  entityId: string,
  mappings: SheetMappings,
): void {
  try {
    storage.setItem(storageKey(entityId), JSON.stringify(mappings));
  } catch (err) {
    console.warn('[wagepro-web] migration: could not save mapping:', err);
  }
}
